import { useEffect, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import {
  isPushSupported, currentPermission, isSubscribed,
  enablePush, disablePush, getPushPref, setPushPref,
} from '../../services/push.js'
import { useTranslation } from 'react-i18next'

/**
 * Native push-notification toggle — PER USER (keyed by their id), defaulting ON.
 * Used in the notification-bell dropdown (compact) and in Profile Settings (row).
 * Hidden entirely on browsers that don't support push.
 */
export default function PushToggle({ variant = 'compact' }) {
  const { t } = useTranslation('ui')
  const { user } = useAuth()
  const [supported] = useState(isPushSupported())
  const [on, setOn] = useState(true)          // shows the user's preference (default ON)
  const [busy, setBusy] = useState(false)
  const [hint, setHint] = useState('')

  useEffect(() => {
    if (!supported || !user?.id) return
    const pref = getPushPref(user.id)          // default ON
    setOn(pref)
    ;(async () => {
      const sub = await isSubscribed()
      if (!pref) return
      if (currentPermission() === 'granted') {
        if (!sub) await enablePush()           // wanted + allowed → make sure we're subscribed
      } else if (currentPermission() === 'default') {
        setHint('Allow notifications when your browser asks, to receive them.')
      } else {
        setHint('Notifications are blocked in your browser settings — allow them for this site to receive alerts.')
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported, user?.id])

  if (!supported) return null

  const toggle = async () => {
    setBusy(true); setHint('')
    if (on) {
      await disablePush(); setPushPref(user?.id, false); setOn(false)
    } else {
      const res = await enablePush()
      if (res.ok) { setPushPref(user?.id, true); setOn(true) }
      else if (res.reason === 'denied') setHint(currentPermission() === 'denied'
        ? 'Blocked in browser settings — allow notifications for this site, then try again.'
        : 'Permission not granted.')
      else if (res.reason === 'not-configured') setHint('Push is not set up on the server yet.')
      else setHint("Couldn't enable notifications. Try again.")
    }
    setBusy(false)
  }

  const btn = (
    <button
      onClick={toggle}
      disabled={busy}
      className={`shrink-0 px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors disabled:opacity-50 ${
        on ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
           : 'bg-blue-600 text-white hover:bg-blue-700'
      }`}
    >
      {busy ? '…' : on ? 'Turn off' : 'Enable'}
    </button>
  )

  if (variant === 'settings') {
    return (
      <div className="border border-gray-200 rounded-lg p-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-gray-800">{t('push_notifications')}</p>
            <p className="text-xs text-gray-500">{on
              ? 'On for this device — you\'ll get alerts even when the app is closed.'
              : 'Off — turn on to get alerts even when the app is closed.'}</p>
          </div>
          {btn}
        </div>
        {hint && <p className="text-[11px] text-amber-600 mt-1">{hint}</p>}
      </div>
    )
  }

  // compact (bell dropdown)
  return (
    <div className="px-4 py-2 border-b border-gray-100 bg-white">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-gray-600">
          {on ? 'Push notifications on for this device' : 'Get alerts even when the app is closed'}
        </span>
        {btn}
      </div>
      {hint && <p className="text-[10px] text-amber-600 mt-1">{hint}</p>}
    </div>
  )
}
